import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import moment from "moment";

import WPInput from "../../packages/WPInput";
import WPInputLabel from "../../packages/WPInputLabel";

import CriticalDeleteDialog from "../../components/CriticalDeleteDialog";
import { DatePickerConfig } from "../../components/DatePopover/DateTimeConfig";
import DupontDatePicker from "../../components/DatePopover/DupontDatePicker";
import DupontRadio from "../../components/DupontRadio";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import { PLANT_INFO, SELECTED_SECTION } from "./plantInformation.config";

import IconWithTooltip from "../../common/styles/components/headings/IconWithTooltip";

import { ValueStrings } from "../../utils/constantsString";

import { updatePlantInformation } from "../../network/cipAPI";

import CAStore from "../../Store/TemplateStore/template.store";

export const store = CAStore;

const PlantInformation = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const plantInfo = PLANT_INFO(t);
  const selectedSection = SELECTED_SECTION(t);

  const {
    plantName,
    plantLocation,
    water_type,
    num_trains,
    tol_num_elements,
    sel_train,
    sel_pass,
    sel_stage,
    sel_pressure_vessels,
    sel_element_involved,
    dataset_name,
    sel_element_type,
    is_dupont_element,
    selTrain: selectedTrain,
    // Threshold values
    max_feed_pressure,
    max_permeate_quality,
    std_warranty_period,
    startup_pressure_drop,
    designed_permeate_flow,
    std_membrane_age,
    max_pressure_drop,
    min_permeate_flow,
    nwp_trigger,
    // Installation date and warranty
    installation_date: storeInstallationDate,
    warranty: storeWarranty,
  } = useSelector((state) => state);

  const plantInfoValues = {
    plantName: plantName,
    plantLocation: plantLocation,
    water_type: water_type,
    num_trains: num_trains,
    tol_num_elements: tol_num_elements,
    sel_train: sel_train,
    sel_pass: sel_pass,
    sel_stage: sel_stage,
    sel_pressure_vessels: sel_pressure_vessels,
    sel_element_involved: sel_element_involved,
    dataset_name: dataset_name,
    sel_element_type: sel_element_type,
    is_dupont_element: is_dupont_element,
  };

  const replaceEmptyWithNull = (attribute) => {
    return attribute !== "" ? attribute : null;
  };

  const plantInformation = async () => {
    let additionErrors = false;

    if (
      (props.ra && installation_date === null) ||
      installation_date === "" ||
      installation_date === undefined
    ) {
      additionErrors = true;
      setInstallationDateError(
        t("plantInformationStrings.installationDateError"),
      );
    }

    if (
      plantInfoValues.plantName &&
      plantInfoValues.plantLocation &&
      props.mandatoryThresholdFilled
    ) {
      if (props.ra && additionErrors) {
        return;
      } else {
        props.setActiveStep(1);
        let payload = null;
        if (props.ra) {
          const currentValues = {
            max_feed_pressure,
            max_permeate_quality,
            std_warranty_period,
            startup_pressure_drop,
            designed_permeate_flow,
            std_membrane_age,
            max_pressure_drop,
            min_permeate_flow,
            nwp_trigger,
          };

          // Only include values that have changed from their original values
          const changedThreshold = Object.entries(currentValues)
            .filter(([key, value]) => {
              const originalValue = originalThresholdValues[key];
              return value !== originalValue && value !== "";
            })
            .reduce(
              (acc, [key, value]) => ({
                ...acc,
                [key]: replaceEmptyWithNull(value),
              }),
              {},
            );

          const formattedInstallationDate = installation_date
            ? moment(installation_date).format(
                ValueStrings.installationDateFormat,
              )
            : "";

          // Only include installation_date and warranty if they have changed
          const hasInstallationDateChanged =
            formattedInstallationDate !== originalInstallationDate;
          const hasWarrantyChanged = warranty !== originalWarranty;

          // Build payload only if there are actual changes
          if (
            Object.keys(changedThreshold).length > 0 ||
            hasInstallationDateChanged ||
            hasWarrantyChanged
          ) {
            payload = {
              ...(Object.keys(changedThreshold).length > 0 && {
                threshold: changedThreshold,
              }),
              ...(hasInstallationDateChanged && {
                installation_date: formattedInstallationDate,
              }),
              ...(hasWarrantyChanged && { warranty }),
            };
          }
        }
        dispatch({
          type: "STORE_PLANT_DETAILS",
          payload: {
            installation_date: moment(installation_date).format(
              ValueStrings.installationDateFormat,
            ),
            warranty: warranty,
          },
        });

        if (payload) {
          try {
            const response = await updatePlantInformation(
              payload,
              selectedTrain,
            );
            if (response?.status !== 200) {
              throw new Error("something went wrong");
            }
          } catch (error) {
            createNotification(
              ERROR_NOTIFICATION,
              t("ValueStrings.somethingWentWrong"),
            );
          }
        }
        setInstallationDateError(null);
      }
    }
  };

  React.useImperativeHandle(ref, () => ({ plantInformation }));
  const [installation_date, setInstallationDate] = useState(
    storeInstallationDate && storeInstallationDate !== "None"
      ? new Date(storeInstallationDate)
      : "",
  );
  const [installationDateError, setInstallationDateError] = useState("");
  const [warranty, setWarranty] = useState(
    storeWarranty?.toString() || "false",
  );
  const [warrantyPopup, setWarrantyPopup] = useState(false);

  useEffect(() => {
    setInstallationDate(
      storeInstallationDate && storeInstallationDate !== "None"
        ? new Date(storeInstallationDate)
        : "",
    );
    setWarranty(storeWarranty?.toString() || "false");
  }, [storeInstallationDate, storeWarranty]);

  const originalThresholdValues = useMemo(
    () => ({
      max_feed_pressure,
      max_permeate_quality,
      std_warranty_period,
      startup_pressure_drop,
      designed_permeate_flow,
      std_membrane_age,
      max_pressure_drop,
      min_permeate_flow,
      nwp_trigger,
    }),
    [sel_train],
  );

  const originalInstallationDate = useMemo(
    () =>
      storeInstallationDate && storeInstallationDate !== "None"
        ? storeInstallationDate
        : "",
    [sel_train],
  );

  const originalWarranty = useMemo(
    () => storeWarranty?.toString() || "false",
    [sel_train],
  );

  const onDateChange = (value) => {
    setInstallationDateError("");
    setInstallationDate(value);
  };
  const handleOptionChange = (event) => {
    const { value } = event.target;
    setWarranty(value);
    if (value === "true") setWarrantyPopup(true);
  };
  const onCloseClicked = () => {
    setWarrantyPopup(false);
  };

  return (
    <div className="ro-card-wrapper">
      <div className="ro-card-header">
        <div className="teal-heading">
          {t("plantInformationStrings.plantInfo")}
        </div>
      </div>
      <div className="plant-grid-container">
        {Object.keys(plantInfo).map((info) => (
          <div className="plant-grid-item">
            <WPInput
              value={plantInfoValues[info]}
              label={plantInfo[info].labelText}
              disabled
            />
          </div>
        ))}

        {props.ra && (
          <>
            <div className="grid-item">
              <DupontDatePicker
                dateFormat={t("ValueStrings.DATE_FORMAT_SMALL")}
                selected={installation_date ? installation_date : ""}
                onChange={onDateChange}
                startDate={new Date()}
                label={t("plantInformationStrings.installation_date")}
                infolabel={t("helpInformation.installationDateHelp")}
                //minDate={cipStartDate}
                maxDate={new Date()}
                className={"width-fill"}
                isRequired
                error={installationDateError}
                placeholderText={t("ValueStrings.selectDate")}
                {...DatePickerConfig}
              />
            </div>
            <div className="grid-item warranty-field-container">
              <div className="info-section">
                <WPInputLabel label={t("plantInformationStrings.warranty")} />
                <IconWithTooltip label={t("helpInformation.warrantyHelp")} />
              </div>
              <div
                key="warrantyGroup"
                onChange={handleOptionChange}
                className=" warranty-options"
              >
                <DupontRadio
                  text={t("ValueStrings.yes")}
                  checked={warranty === "true"}
                  value={"true"}
                  name="warrantyGroup"
                />
                <DupontRadio
                  text={t("ValueStrings.no")}
                  checked={warranty === "false"}
                  value={"false"}
                  name="warrantyGroup"
                />
              </div>
            </div>
            {warrantyPopup && (
              <CriticalDeleteDialog
                type="warning"
                title={t("plantInformationStrings.warrantyNotice")}
                description={t("plantInformationStrings.warrantyWarning")}
                onDialogClose={onCloseClicked}
                cancelBtnText={t("ValueStrings.close")}
              />
            )}
          </>
        )}
      </div>
      <div className="margin-top-20">
        {t("plantInformationStrings.selectedSection")}
        <div className="selected-grid-container">
          {Object.keys(selectedSection).map((info) => (
            <div className="plant-grid-item">
              <WPInput
                value={plantInfoValues[info] ?? "NA"}
                label={selectedSection[info].labelText}
                disabled
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

export default PlantInformation;
